/**
 * @page /mobile (Mobile MA-Portal Home)
 * @description Mobile-optimized dashboard for employee portal.
 *   Stamp clock, quick actions, info cards, attendance streak.
 * @api GET /api/portal/ma-dashboard, GET /api/stempel/mein-status, POST /api/stempel/app-scan
 * @roles portal_mitarbeiter, admin, manager
 * @i18n mobilePortal.home
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { berechnePlanbarAnteile } from '../../utils/urlaubPlanbar';
import { istKioskSession } from '../../utils/workSession';
import SlideToConfirm from '../../components/SlideToConfirm';

const API = import.meta.env.VITE_API_URL || '';

/* ── helpers ── */

function pad(n) { return String(n).padStart(2, '0'); }

function formatTime(iso) {
  if (!iso) return '--:--';
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtDateShort(dateStr) {
  if (!dateStr) return '';
  const s = String(dateStr).slice(0, 10);
  const d = new Date(s + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function minutesToHM(min) {
  if (min == null) return '--';
  if (min <= 0) return '0h 0m';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${h}h ${m}m`;
}

/* ── component ── */

export default function MobileHome() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState(null);
  const [stempel, setStempel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);
  const [krankmeldung, setKrankmeldung] = useState(null); // aktive Krankmeldung
  const [grundPrompt, setGrundPrompt] = useState(null); // { aktion, warning, minutenGearbeitet, minutenLimit }
  const [grundInput, setGrundInput] = useState('');
  const [gpsVerweigert, setGpsVerweigert] = useState(false); // #1813

  /* ── data fetching ── */

  const fetchAll = useCallback(async () => {
    try {
      const [dashRes, stRes, kmRes] = await Promise.all([
        fetch(`${API}/api/portal/ma-dashboard`, { credentials: 'include' }),
        fetch(`${API}/api/stempel/mein-status`, { credentials: 'include' }),
        fetch(`${API}/api/portal/krankmeldung/aktiv`, { credentials: 'include' }),
      ]);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (stRes.ok) setStempel(await stRes.json());
      if (kmRes.ok) {
        const kmData = await kmRes.json();
        setKrankmeldung(kmData.aktiv ? kmData.krankmeldung : null);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // auto-refresh every 60s
  useEffect(() => {
    const iv = setInterval(fetchAll, 60_000);
    return () => clearInterval(iv);
  }, [fetchAll]);

  // toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  /* ── Standortpflicht (#1813) ── */

  // Die Position wird NUR angefordert, wenn fuer diese Person Standortpflicht gilt —
  // wer ortsunabhaengig stempeln darf, wird gar nicht erst nach seinem Aufenthaltsort
  // gefragt. Der Server entscheidet, nicht dieses Flag (AK5); hier steuert es nur, ob
  // ueberhaupt eine Position erhoben wird.
  const standortPflicht = stempel?.standort_pflicht === true;

  const holePosition = useCallback(() => new Promise((resolve) => {
    if (!navigator.geolocation) { setGpsVerweigert(true); return resolve(null); }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGpsVerweigert(false); resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }); },
      () => { setGpsVerweigert(true); resolve(null); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }), []);

  // Berechtigung frueh erfragen, damit der Browser-Dialog nicht mitten in der
  // Wischgeste aufpoppt und der MA vor dem Stempeln sieht, dass etwas fehlt.
  useEffect(() => { if (standortPflicht) holePosition(); }, [standortPflicht, holePosition]);

  /* ── stamp action ── */

  // #1812: liefert `true` NUR, wenn der Server den Stempel wirklich bestaetigt hat.
  // Der Slide-to-Confirm-Regler haengt daran — ohne dieses Signal koennte er nach einem
  // Serverfehler nicht zurueckfedern und bliebe faelschlich eingerastet stehen (AK3).
  const doStempel = async (aktion, pauseGrund = null) => {
    setActionLoading(aktion);
    try {
      const body = { aktion };
      if (pauseGrund) {
        body.pause_grund = pauseGrund;
        body.grund_kategorie = 'sonstiges';
      }
      // #1813 — nur bei Standortpflicht. Dass der Browser die Freigabe verweigert, weiss
      // ausschliesslich der Client; alle uebrigen Ablehnungen formuliert der Server, damit
      // es fuer die Standortpflicht nur EINEN Meldungstext gibt.
      if (standortPflicht) {
        const pos = await holePosition();
        if (!pos) {
          setToast({ type: 'error', msg: t('mobilePortal.home.standortBlockiert', 'Für dich gilt Standortpflicht beim Stempeln. Bitte erlaube den Standortzugriff in den Website-Einstellungen deines Browsers — oder stemple am Terminal.') });
          return false;
        }
        body.latitude = pos.latitude;
        body.longitude = pos.longitude;
      }
      const res = await fetch(`${API}/api/stempel/app-scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      // Backend fordert Grund ein (Pause vor Ablauf X Minuten) → In-App Modal
      if (res.ok && data.requires_reason) {
        setActionLoading(null);
        setGrundInput('');
        setGrundPrompt({
          aktion,
          warning: data.warning || null,
          minutenGearbeitet: data.minuten_gearbeitet ?? null,
          minutenLimit: data.minuten_limit ?? null,
        });
        return false;
      }
      // Ein gebuchter Stempel ist die EINZIGE Erfolgsbedingung: `erfolg: true` sendet der
      // Server ausschliesslich nach dem INSERT. Der Endpoint kennt weitere 200er, die KEIN
      // Stempel sind — `blocked` (Ganztags-Abwesenheit #822, ausserhalb Arbeitszeitmodell
      // #848) und die Status-Antwort ohne Aktion. Ein Ausschlussverfahren („alles ausser
      // erfolg === false ist Erfolg") meldet die faelschlich als gebucht.
      if (res.ok && data.erfolg === true) {
        setToast({ type: 'success', msg: data.nachricht || data.message || t('mobilePortal.home.stampSuccess', 'Erfolgreich gestempelt') });
        // Erst den servergedeckten Zustand laden, dann Erfolg melden — sonst rastet der
        // Slider ein, waehrend die Karte noch den alten Status zeigt.
        await fetchAll();
        return true;
      }
      setToast({ type: 'error', msg: data.rule_meldung || data.error || data.message || data.hinweis || t('mobilePortal.home.stampError', 'Fehler beim Stempeln') });
      return false;
    } catch {
      setToast({ type: 'error', msg: t('mobilePortal.home.stampError', 'Fehler beim Stempeln') });
      return false;
    } finally {
      setActionLoading(null);
    }
  };

  const submitGrund = () => {
    if (!grundPrompt) return;
    const grund = (grundInput || '').trim();
    if (!grund) return;
    const aktion = grundPrompt.aktion;
    setGrundPrompt(null);
    setGrundInput('');
    doStempel(aktion, grund);
  };

  const cancelGrund = () => {
    setGrundPrompt(null);
    setGrundInput('');
    setToast({ type: 'error', msg: t('mobilePortal.home.pauseCancelled', 'Pause abgebrochen') });
  };

  /* ── Krankmeldung actions ── */

  const doGesundmelden = async () => {
    setActionLoading('gesund');
    try {
      const res = await fetch(`${API}/api/portal/gesundmeldung`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok) {
        setToast({ type: 'success', msg: t('mobilePortal.home.wellAgain', 'Gute Besserung! Krankmeldung abgeschlossen.') });
        fetchAll();
      } else {
        setToast({ type: 'error', msg: data.error || t('mobilePortal.home.sickError', 'Fehler') });
      }
    } catch {
      setToast({ type: 'error', msg: t('mobilePortal.home.sickError', 'Fehler') });
    } finally {
      setActionLoading(null);
    }
  };

  /* ── Arztnachweis Upload ── */
  const doArztnachweisUpload = async (file) => {
    if (!krankmeldung?.id || !file) return;
    setActionLoading('arzt');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/portal/krankmeldung/${krankmeldung.id}/dokument?src=urlaub_antraege`, {
        method: 'PUT',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (res.ok) {
        setToast({ type: 'success', msg: t('mobilePortal.home.certUploaded', 'Arztnachweis hochgeladen') });
        fetchAll();
      } else {
        setToast({ type: 'error', msg: data.error || t('mobilePortal.home.certError', 'Fehler beim Upload') });
      }
    } catch {
      setToast({ type: 'error', msg: t('mobilePortal.home.certError', 'Fehler beim Upload') });
    } finally {
      setActionLoading(null);
    }
  };

  /* ── derived values ── */

  const isPause = stempel?.status === 'in_pause';
  const isStamped = stempel?.status === 'eingestempelt' || isPause;
  const stampSince = stempel?.seit ? formatTime(stempel.seit) : null;

  // #1812: Das geteilte Terminal rendert DIESE Seite (App.jsx routet PIN-Sitzungen
  // ebenfalls auf /portal) — dort bleibt der Button, weil es schnell und im Stehen
  // bedient wird. EINE Definition der Shared-Device-Session: istKioskSession (#1809).
  const istKiosk = istKioskSession(user);

  const zeiten = dashboard?.zeiten_summary || {};
  // #1543 — individuelles Tages-SOLL aus dem Backend; KEIN 8h-Pauschal-Fallback mehr.
  const sollMin = zeiten.soll_minuten ?? null;
  // #1695 (P4b): ma-dashboard liefert nur SOLL-Felder — der echte Ist-Wert kommt aus
  // /api/stempel/mein-status (zählt laufende Zeit bis "jetzt" mit; 60s-Auto-Refresh oben).
  const istMin = stempel?.arbeitszeit_min ?? zeiten.ist_minuten ?? zeiten.ist ?? 0;
  const progress = sollMin > 0 ? Math.min((istMin / sollMin) * 100, 100) : 0;

  // #1782 — Kachel zeigt "Zum Planen verfügbar" (identisch zum Abwesenheiten-Hero),
  // nicht mehr den Saldo. Wert kommt aus demselben Payload-Feld, keine eigene Rechnung.
  const urlaubSummary = dashboard?.urlaub_summary || null;
  const zeigePlanbarKachel = urlaubSummary != null && urlaubSummary.portal_zeige_planbar !== false;
  const { planbar: urlaubPlanbarRaw } = berechnePlanbarAnteile(urlaubSummary);
  const urlaubPlanbar = urlaubSummary ? (Math.round(urlaubPlanbarRaw * 10) / 10).toLocaleString('de-AT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '--';
  const ungeleseneNews = dashboard?.ungelesene_news ?? 0;
  const gourmetUrl = dashboard?.gourmet_portal_url || null; // #1549 Essensbestellung (extern, konfigurierbar)

  // #1543 — Card zeigt kumulierten ZA-Saldo (wie Admin-Sicht), nicht nur Monats-Delta.
  const zkDelta = dashboard?.zeitkonto_summary?.za_saldo;
  const zkDeltaLabel = zkDelta != null
    ? `${zkDelta > 0 ? '+' : ''}${zkDelta.toFixed(1).replace('.', ',')}h`
    : '--';

  const schichtWocheRaw = dashboard?.schichtplan_woche;
  const schichtWoche = Array.isArray(schichtWocheRaw) ? schichtWocheRaw : (schichtWocheRaw?.schichten || []);
  const heute = new Date();
  const heuteTag = heute.getDay(); // 0=Sun
  const naechsteSchicht = schichtWoche.find(s => {
    const tag = s.wochentag ?? s.tag ?? s.datum;
    if (typeof tag === 'number') return tag === heuteTag;
    if (typeof tag === 'string' && tag.length === 10) return tag === heute.toISOString().slice(0, 10);
    return tag === (['So','Mo','Di','Mi','Do','Fr','Sa'][heuteTag]);
  });
  const schichtLabel = naechsteSchicht?.schicht_name || naechsteSchicht?.schicht || null;

  /* ── greeting ── */

  const hour = heute.getHours();
  const greeting = hour < 12
    ? t('mobilePortal.home.goodMorning', 'Guten Morgen')
    : hour < 18
      ? t('mobilePortal.home.goodDay', 'Hallo')
      : t('mobilePortal.home.goodEvening', 'Guten Abend');
  const userName = user?.vorname || user?.name?.split(' ')[0] || '';

  /* ── render ── */

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--bg-page, #F8F9FC)' }}>
        <div className="animate-spin w-8 h-8 border-3 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pb-8"
      style={{
        background: 'var(--bg-page, #F8F9FC)',
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 16,
      }}
    >
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 left-4 right-4 z-50 px-4 py-3 rounded-2xl text-sm font-medium text-white shadow-sm transition-all ${
          toast.type === 'success' ? 'bg-[#338edc]' : 'bg-red-500'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Header / Greeting */}
      <div className="px-4 pt-6 pb-2">
        <p className="text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
          {greeting}
        </p>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
          {userName}
        </h1>
      </div>

      {/* ── 1. Stempel Section (nur wenn erlaubt) ── */}
      {dashboard?.profil?.stempel_portal_erlaubt && (
      <div className="px-4 mt-2">
        <div
          className="rounded-2xl p-5"
          style={{
            background: 'var(--bg-card, #FFFFFF)',
            border: '1px solid var(--border-card, #E5E7EB)',
          }}
        >
          {/* Status indicator */}
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-3 h-3 rounded-full ${isStamped ? (isPause ? 'bg-amber-400' : 'bg-[#338edc]') : 'bg-[var(--border-default,#D1D5DB)]'}`} />
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {isStamped
                ? isPause
                  ? t('mobilePortal.home.onPause', 'In Pause')
                  : `${t('mobilePortal.home.stampedIn', 'Eingestempelt seit')} ${stampSince}`
                : t('mobilePortal.home.notStamped', 'Nicht eingestempelt')
              }
            </span>
          </div>

          {/* Progress bar */}
          <div className="mb-1 flex items-center justify-between text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
            <span>{t('mobilePortal.home.workTime', 'Arbeitszeit')}</span>
            <span>{minutesToHM(istMin)} / {minutesToHM(sollMin)}</span>
          </div>
          <div className="w-full h-2.5 rounded-full bg-[var(--bg-inset,#F3F4F6)] mb-4 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${progress}%`,
                background: '#338edc',
              }}
            />
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            {!isStamped ? (
              istKiosk ? (
              <button
                onClick={() => doStempel('arbeit_start')}
                disabled={!!actionLoading}
                className="flex-1 py-3 rounded-xl text-white font-semibold text-sm transition-all active:scale-[0.97] disabled:opacity-50"
                style={{ background: '#338edc' }}
              >
                {actionLoading === 'arbeit_start'
                  ? t('mobilePortal.home.loading', 'Wird gestempelt...')
                  : t('mobilePortal.home.stampIn', 'Einstempeln')
                }
              </button>
              ) : (
              <div className="flex-1">
                <SlideToConfirm
                  color="#338edc"
                  disabled={!!actionLoading}
                  onConfirm={() => doStempel('arbeit_start')}
                  label={t('mobilePortal.home.slideToStampIn', 'Zum Einstempeln wischen')}
                  loadingLabel={t('mobilePortal.home.loading', 'Wird gestempelt...')}
                  successLabel={t('mobilePortal.home.stampDone', 'Gestempelt')}
                  ariaLabel={t('mobilePortal.home.slideAriaIn', 'Einstempeln — wischen oder Enter drücken')}
                />
              </div>
              )
            ) : isPause ? (
              <button
                onClick={() => doStempel(stempel?.letzter_stempel?.typ === 'mittagspause' ? 'mittag_ende' : 'pause_ende')}
                disabled={!!actionLoading}
                className="flex-1 py-3 rounded-xl text-white font-semibold text-sm transition-all active:scale-[0.97] disabled:opacity-50"
                style={{ background: '#338edc' }}
              >
                {actionLoading
                  ? t('mobilePortal.home.loading', 'Wird gestempelt...')
                  : t('mobilePortal.home.resumeWork', 'Pause beenden')
                }
              </button>
            ) : (
              <div className="flex-1 space-y-2.5">
                {/* #1696 P4a: beide Pausenarten wählbar — korrekter Typ wird ans Backend durchgereicht */}
                <div className="flex gap-3">
                  <button
                    onClick={() => doStempel('kurze_pause')}
                    disabled={!!actionLoading}
                    className="flex-1 py-3 rounded-xl font-semibold text-sm transition-all active:scale-[0.97] disabled:opacity-50"
                    style={{ background: '#338edc20', color: '#338edc' }}
                  >
                    {actionLoading === 'kurze_pause'
                      ? '...'
                      : t('mobilePortal.home.pauseShort', 'Kurze Pause')
                    }
                  </button>
                  <button
                    onClick={() => doStempel('mittagspause')}
                    disabled={!!actionLoading}
                    className="flex-1 py-3 rounded-xl font-semibold text-sm transition-all active:scale-[0.97] disabled:opacity-50"
                    style={{ background: '#338edc20', color: '#338edc' }}
                  >
                    {actionLoading === 'mittagspause'
                      ? '...'
                      : t('mobilePortal.home.pauseLunch', 'Mittagspause')
                    }
                  </button>
                </div>
                {istKiosk ? (
                <button
                  onClick={() => doStempel('arbeitsende')}
                  disabled={!!actionLoading}
                  className="w-full py-3 rounded-xl font-semibold text-sm transition-all active:scale-[0.97] disabled:opacity-50 border"
                  style={{ background: '#338edc10', color: '#338edc', borderColor: '#338edc30' }}
                >
                  {actionLoading === 'arbeitsende'
                    ? '...'
                    : t('mobilePortal.home.stampOut', 'Feierabend')
                  }
                </button>
                ) : (
                <SlideToConfirm
                  /* Dunkleres Amber als der Badge-Ton #F59E0B: die Bahn ist jetzt
                     flaechig gefuellt, weisse Schrift auf #F59E0B waere zu blass. */
                  color="#D97706"
                  disabled={!!actionLoading}
                  onConfirm={() => doStempel('arbeitsende')}
                  label={t('mobilePortal.home.slideToStampOut', 'Zum Ausstempeln wischen')}
                  loadingLabel={t('mobilePortal.home.loading', 'Wird gestempelt...')}
                  successLabel={t('mobilePortal.home.stampDone', 'Gestempelt')}
                  ariaLabel={t('mobilePortal.home.slideAriaOut', 'Ausstempeln — wischen oder Enter drücken')}
                />
                )}
              </div>
            )}
          </div>

          {/* #1813 — Standortpflicht: erklaeren, was fehlt und was zu tun ist, statt nur
              „GPS-Position erforderlich". Erscheint nur, wenn die Pflicht wirklich gilt. */}
          {standortPflicht && gpsVerweigert && (
            <p className="mt-3 text-xs leading-snug" style={{ color: 'var(--status-pending)' }}>
              {t('mobilePortal.home.standortHinweis', 'Für dich gilt Standortpflicht beim Stempeln. Bitte erlaube den Standortzugriff in den Website-Einstellungen deines Browsers — oder stemple am Terminal.')}
            </p>
          )}
        </div>
      </div>
      )}

      {/* ── 1b. Krankmeldung Banner ── */}
      {krankmeldung && (
        <div className="px-4 mt-3">
          <div
            className="rounded-2xl p-4"
            style={{
              background: '#FEF2F2',
              border: '1px solid #FECACA',
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xl">🩺</span>
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: '#991B1B' }}>
                  {t('mobilePortal.home.sickBanner', 'Du bist krank gemeldet')}
                </p>
                <p className="text-xs mt-0.5" style={{ color: '#B91C1C' }}>
                  {t('mobilePortal.home.sickSince', 'Seit')}: {krankmeldung.von_datum ? fmtDateShort(krankmeldung.von_datum) : '—'}
                </p>
              </div>
            </div>
            {/* Arztnachweis Upload Banner */}
            {!krankmeldung.arztnachweis_datei && (
              <div className="mb-2 p-2.5 rounded-lg" style={{ background: '#FEF3C7', border: '1px solid #FDE68A' }}>
                <p className="text-xs font-medium mb-2" style={{ color: '#92400E' }}>
                  {t('mobilePortal.home.certMissing', 'Bitte lade eine ärztliche Bestätigung hoch.')}
                </p>
                <label
                  className="flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all active:scale-[0.97]"
                  style={{ background: '#FFFFFF', color: '#92400E', border: '1px solid #FDE68A' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  {actionLoading === 'arzt' ? '...' : t('mobilePortal.home.certUpload', 'Arztnachweis hochladen')}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => { if (e.target.files?.[0]) doArztnachweisUpload(e.target.files[0]); }} />
                </label>
              </div>
            )}
            {krankmeldung.arztnachweis_datei && (
              <div className="mb-2 flex items-center gap-2 text-xs" style={{ color: '#065F46' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                {t('mobilePortal.home.certDone', 'Arztnachweis vorhanden')}
              </div>
            )}
            <button
              onClick={doGesundmelden}
              disabled={actionLoading === 'gesund'}
              className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-[0.97] disabled:opacity-50"
              style={{ background: '#FFFFFF', color: '#991B1B', border: '1px solid #FECACA' }}
            >
              {actionLoading === 'gesund'
                ? '...'
                : t('mobilePortal.home.wellButton', 'Ich bin wieder gesund')}
            </button>
          </div>
        </div>
      )}

      {/* ── 2. Quick Actions ── */}
      <div className="px-4 mt-5">
        <h2 className="text-sm font-semibold mb-2.5" style={{ color: 'var(--text-secondary, #4B5563)' }}>
          {t('mobilePortal.home.quickActions', 'Schnellaktionen')}
        </h2>
        <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {!krankmeldung && (
            <button
              // #1696 P1: volles Krankenstand-Formular (Dauer/Upload/Grund) statt Direkt-POST ohne Felder
              onClick={() => navigate('/portal/abwesend?typ=krank')}
              className="flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-medium border transition-all active:scale-[0.95]"
              style={{ background: '#EF444414', color: '#EF4444', borderColor: '#EF444430' }}
            >
              {t('mobilePortal.home.reportSick', 'Krank melden')}
            </button>
          )}
          <button
            onClick={() => navigate('/portal/abwesend?typ=urlaub')}
            className="flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-medium border transition-all active:scale-[0.95]"
            style={{ background: '#338edc10', color: '#338edc', borderColor: '#338edc30' }}
          >
            {t('mobilePortal.home.vacation', 'Urlaub')}
          </button>
          <button
            onClick={() => navigate('/portal/abwesend?typ=sonstig')}
            className="flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-medium border transition-all active:scale-[0.95]"
            style={{ background: '#338edc10', color: '#338edc', borderColor: '#338edc30' }}
          >
            {t('mobilePortal.home.absent', 'Abwesend')}
          </button>
        </div>
      </div>

      {/* ── 3. Info Cards (2x2 Grid) ── */}
      <div className="px-4 mt-5">
        <h2 className="text-sm font-semibold mb-2.5" style={{ color: 'var(--text-secondary, #4B5563)' }}>
          {t('mobilePortal.home.overview', 'Übersicht')}
        </h2>
        <div className="grid grid-cols-2 gap-3">
          {/* #1782 — Zum Planen verfügbar (Wert + Sichtbarkeit wie Abwesenheiten-Hero) */}
          {zeigePlanbarKachel && (
          <Link
            to="/portal/urlaub"
            className="rounded-2xl p-4 block transition-all active:scale-[0.97]"
            style={{
              background: 'var(--bg-card, #FFFFFF)',
              border: '1px solid var(--border-card, #E5E7EB)',
            }}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: '#EFF6FF' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#338edc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" />
              </svg>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('mobilePortal.home.vacationPlannable', 'Zum Planen verfügbar')}
            </p>
            <p className="text-lg font-semibold mt-0.5" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {urlaubPlanbar} {t('mobilePortal.home.days', 'Tage')}
            </p>
            {urlaubSummary?.urlaubsjahr_bis && (
              <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                {t('mobilePortal.home.vacationPlannableUntil', {
                  datum: fmtDateShort(urlaubSummary.urlaubsjahr_bis),
                  defaultValue: 'bis {{datum}}',
                })}
              </p>
            )}
          </Link>
          )}

          {/* Zeitkonto */}
          <Link
            to="/portal/zeiten"
            className="rounded-2xl p-4 block transition-all active:scale-[0.97]"
            style={{
              background: 'var(--bg-card, #FFFFFF)',
              border: '1px solid var(--border-card, #E5E7EB)',
            }}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: '#EFF6FF' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#338edc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('mobilePortal.home.timeAccount', 'Zeitkonto')}
            </p>
            <p className="text-lg font-semibold mt-0.5" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {zkDeltaLabel}
            </p>
          </Link>

          {/* News */}
          <Link
            to="/portal/news"
            className="rounded-2xl p-4 block transition-all active:scale-[0.97]"
            style={{
              background: 'var(--bg-card, #FFFFFF)',
              border: '1px solid var(--border-card, #E5E7EB)',
            }}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: '#EFF6FF' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#338edc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
                <path d="M18 14h-8" /><path d="M15 18h-5" /><path d="M10 6h8v4h-8V6Z" />
              </svg>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('mobilePortal.home.news', 'News')}
            </p>
            <p className="text-lg font-semibold mt-0.5" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {ungeleseneNews > 0
                ? `${ungeleseneNews} ${t('mobilePortal.home.unread', 'ungelesen')}`
                : t('mobilePortal.home.allRead', 'Alles gelesen')
              }
            </p>
          </Link>

          {/* Schicht */}
          <div
            className="rounded-2xl p-4"
            style={{
              background: 'var(--bg-card, #FFFFFF)',
              border: '1px solid var(--border-card, #E5E7EB)',
            }}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: '#EFF6FF' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#338edc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('mobilePortal.home.shift', 'Schicht')}
            </p>
            <p className="text-lg font-semibold mt-0.5 truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {schichtLabel || t('mobilePortal.home.noShift', 'Keine Schicht')}
            </p>
          </div>

          {/* Schulungen (#919a) */}
          <Link
            to="/portal/schulungen"
            className="rounded-2xl p-4 block transition-all active:scale-[0.97]"
            style={{
              background: 'var(--bg-card, #FFFFFF)',
              border: '1px solid var(--border-card, #E5E7EB)',
            }}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: '#EFF6FF' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#338edc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="6" /><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
              </svg>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('mobilePortal.home.schulungen', 'Schulungen')}
            </p>
            <p className="text-lg font-semibold mt-0.5 truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {dashboard?.offene_schulungen > 0
                ? `${dashboard.offene_schulungen} ${t('mobilePortal.home.offen', 'offen')}`
                : t('mobilePortal.home.alleErledigt', 'Alles erledigt')}
            </p>
          </Link>

          {/* Vorschlaege (#920) */}
          <Link
            to="/portal/vorschlaege"
            className="rounded-2xl p-4 block transition-all active:scale-[0.97]"
            style={{
              background: 'var(--bg-card, #FFFFFF)',
              border: '1px solid var(--border-card, #E5E7EB)',
            }}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: '#FEF3C7' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.5.5.8 1 .9 1.6l.2 1.7h5.8l.2-1.7c.1-.6.4-1.1.9-1.6A7 7 0 0 0 12 2z" />
              </svg>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('mobilePortal.home.vorschlaege', 'Vorschlaege')}
            </p>
            <p className="text-lg font-semibold mt-0.5 truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {t('mobilePortal.home.einreichen', 'Idee einreichen')}
            </p>
          </Link>

          {/* Essensbestellung (#1549) — externer GOURMET-Link, nur wenn konfiguriert */}
          {gourmetUrl && (
            <a
              href={gourmetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-2xl p-4 block transition-all active:scale-[0.97]"
              style={{
                background: 'var(--bg-card, #FFFFFF)',
                border: '1px solid var(--border-card, #E5E7EB)',
              }}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: '#ECFDF5' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 2v7c0 1.1.9 2 2 2h0a2 2 0 0 0 2-2V2" /><path d="M7 2v20" /><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
                </svg>
              </div>
              <p className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('mobilePortal.home.essensbestellung', 'Essensbestellung')}
              </p>
              <p className="text-lg font-semibold mt-0.5 truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                {t('mobilePortal.home.essenBestellen', 'Jetzt bestellen')}
              </p>
            </a>
          )}
        </div>
      </div>

      {/* ── Pause-Grund Modal ── */}
      {grundPrompt && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-6 pt-4"
          style={{ background: 'rgba(17, 24, 39, 0.55)' }}
          onClick={cancelGrund}
        >
          <div
            className="w-full max-w-md rounded-2xl p-5"
            style={{ background: 'var(--bg-card, #FFFFFF)', border: '1px solid var(--border-card, #E5E7EB)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {t('mobilePortal.home.pauseReasonTitle', 'Pause-Grund erforderlich')}
            </h2>
            <p className="text-sm mb-3" style={{ color: 'var(--text-secondary, #4B5563)' }}>
              {grundPrompt.warning || t('mobilePortal.home.pauseEarlyWarn', 'Pause vor Ablauf der Wartezeit')}
            </p>
            {grundPrompt.minutenGearbeitet != null && grundPrompt.minutenLimit != null && (
              <p className="text-xs mb-3" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('mobilePortal.home.pauseReasonStats', 'Gearbeitet')}: <b>{grundPrompt.minutenGearbeitet} min</b> / {t('mobilePortal.home.pauseReasonLimit', 'Limit')}: <b>{grundPrompt.minutenLimit} min</b>
              </p>
            )}
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary, #4B5563)' }}>
              {t('mobilePortal.home.pauseReasonPrompt', 'Bitte kurz begründen:')}
            </label>
            <textarea
              autoFocus
              value={grundInput}
              onChange={(e) => setGrundInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  submitGrund();
                }
              }}
              rows={3}
              placeholder={t('mobilePortal.home.pauseReasonPlaceholder', 'z.B. Rauchpause, WC, kurzer Anruf …')}
              className="w-full rounded-xl px-3 py-2.5 text-sm outline-none mb-4"
              style={{
                background: 'var(--bg-inset, #F8F9FC)',
                border: '1px solid var(--border-default, #D1D5DB)',
                color: 'var(--text-primary, #1A1A2E)',
                resize: 'none',
                fontFamily: 'inherit',
              }}
            />
            <div className="flex gap-3">
              <button
                onClick={cancelGrund}
                className="flex-1 py-3 rounded-xl font-semibold text-sm transition-all active:scale-[0.97] border"
                style={{ background: '#FFFFFF', color: 'var(--text-secondary, #4B5563)', borderColor: 'var(--border-default, #D1D5DB)' }}
              >
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button
                onClick={submitGrund}
                disabled={!grundInput.trim()}
                className="flex-1 py-3 rounded-xl text-white font-semibold text-sm transition-all active:scale-[0.97] disabled:opacity-50"
                style={{ background: '#338edc' }}
              >
                {t('mobilePortal.home.pauseReasonConfirm', 'Pause bestätigen')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
